/**
 * TaahhutnameDurumuPaneli.jsx - the staff side of the commitment (taahhütname) workflow, in the "Proje Durumu" tab of the
 * stand detail (backlog #217, built on the backend of #216 and #505).
 *
 * It draws the body of GET /projects/{id}/taahhutnameler as it is - no state is derived here any more (the old
 * deriveCommitmentStage guessed from the files under title 5). Per taahhütname (İSG / Elektrik / Statik):
 *   1. the source document: the chosen one, or the candidates to pick from (PUT /projects/{id}/files/{fileId}/choose);
 *   2. "Taahhütnameyi Göster": the generated, printable page (TaahhutnameBelgesi), with "PDF indir: Türkçe | English" (#666);
 *   3. the company's approval, with who and when and the wording version it saw;
 *   4. the optional signed copy: approved or rejected here, with the reason (PUT .../files/{fileId}/approval);
 *   5. Elektrik only: the signature level and the electrician's signature, recorded on the fairground (#505);
 *   6. its trail (who did what, newest first).
 * On top, how many of the REQUIRED ones count for the İnşa İzni. The page keeps the data and does the calls; this panel
 * only asks (onChoose, onReviewFile, onElektrikci*), so it is tested with plain props.
 */
import React, { useState } from "react";
import { Alert, Badge, Button, Card, CardBody, Col, Input, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { Camera, Check, ExternalLink, FileText, History, Trash2, UserCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { StatusTile, COMMITMENT_ICONS } from "./StandPills";
import { COMMITMENT_STATE } from "../../services/standOverviewService";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import TaahhutnameBelgesi from "./TaahhutnameBelgesi";
import ElektrikciImzaModal from "./ElektrikciImzaModal";
import {
  kaynakAdaylari, sartOzeti, gecmisOf, olayAnahtari, seviyeAnahtari, fmtZaman,
} from "./taahhutnameYardimci";

/** The optional signed copy: file, review status, reason, and the staff's Onayla / Reddet (with a reason). */
function ImzaliKopya({ kopya, busy, onReviewFile, onPreview }) {
  const { t } = useTranslation("projeler");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  if (!kopya) {
    return <div className="text-muted small fst-italic">{t("taahhutnameDurumu.imzaliKopyaYok")}</div>;
  }
  const info = FILE_APPROVAL_STATUS[kopya.approvalStatus ?? 0] ?? FILE_APPROVAL_STATUS[0];
  return (
    <div data-testid="imzali-kopya">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <Button
          color="link" size="sm" className="p-0 text-truncate text-start" style={{ maxWidth: "70%" }}
          title={kopya.originalFileName}
          onClick={() => onPreview({ originalFileName: kopya.originalFileName, storedFileName: kopya.storedFileName })}
        >
          <ExternalLink size={12} className="me-1" />{kopya.originalFileName}
        </Button>
        <Badge color={info.color} className="fw-normal flex-shrink-0">{info.label}</Badge>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {t("taahhutnameDurumu.yuklendi", { tarih: fmtZaman(kopya.uploadedAt) })}
        {(kopya.reviewedAt || kopya.reviewedByName) && (
          <> · {t("taahhutnameDurumu.inceleyen", { ad: kopya.reviewedByName ?? "—", tarih: fmtZaman(kopya.reviewedAt) })}</>
        )}
      </div>
      {Number(kopya.approvalStatus) === 2 && kopya.rejectReason && (
        <div className="text-danger" style={{ fontSize: 11 }}>{t("taahhutnameDurumu.redGerekcesi", { gerekce: kopya.rejectReason })}</div>
      )}
      <div className="d-flex gap-2 mt-2">
        <Button
          color="success" size="sm" outline={Number(kopya.approvalStatus) !== 1} disabled={busy}
          onClick={() => { setRejecting(false); onReviewFile(kopya.fileId, 1); }} data-testid="imzali-onayla"
        >
          <Check size={12} className="me-1" />{t("taahhutnameDurumu.imzaliyiOnayla")}
        </Button>
        <Button
          color="danger" size="sm" outline={Number(kopya.approvalStatus) !== 2} disabled={busy}
          onClick={() => setRejecting(true)} data-testid="imzali-reddet"
        >
          <X size={12} className="me-1" />{t("taahhutnameDurumu.imzaliyiReddet")}
        </Button>
      </div>
      {rejecting && (
        <div className="mt-2" data-testid="imzali-red-formu">
          <Input
            type="textarea" rows="2" value={reason} maxLength={500}
            placeholder={t("standOverviewDetail.redGerekcesiPlaceholder")}
            onChange={(e) => setReason(e.target.value)} data-testid="imzali-red-gerekce"
          />
          <div className="d-flex gap-2 mt-1">
            <Button
              color="danger" size="sm" disabled={busy} data-testid="imzali-red-kaydet"
              onClick={async () => { await onReviewFile(kopya.fileId, 2, reason.trim()); setRejecting(false); setReason(""); }}
            >
              {t("standOverviewDetail.reddiKaydet")}
            </Button>
            <Button color="light" size="sm" onClick={() => { setRejecting(false); setReason(""); }}>{t("common:iptal")}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Elektrik only: the signature level and the electrician's signature (#505), recorded by staff. */
function ElektrikciBolumu({ tn, busy, onAc, onKaldir, onFoto, onPreview }) {
  const { t } = useTranslation("projeler");
  const [kaldiriyor, setKaldiriyor] = useState(false);
  const [kaldirNotu, setKaldirNotu] = useState("");
  const seviye = seviyeAnahtari(tn.level);
  const imza = tn.elektrikciImzasi;
  return (
    <div className="mt-3 pt-2 border-top" data-testid="elektrikci-bolumu">
      <div className="small fw-semibold mb-1">{t("taahhutnameDurumu.elektrikci.bolum")}</div>
      {seviye ? (
        <Badge color={tn.level === "DEKORATOR_ELEKTRIKCI" ? "success" : "info"} className="fw-normal" data-testid="elektrikci-seviye">
          {t(seviye)}
        </Badge>
      ) : (
        <div className="text-muted small fst-italic">{t("taahhutnameDurumu.elektrikci.dekoratorBekleniyor")}</div>
      )}

      {imza && (
        <div className="mt-2 small" data-testid="elektrikci-imza">
          <div className="fw-medium"><UserCheck size={13} className="me-1" />{imza.name}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>
            {t("taahhutnameDurumu.elektrikci.imzaladi", { tarih: fmtZaman(imza.signedAt) })}
            {imza.recordedByName ? ` · ${t("taahhutnameDurumu.elektrikci.kaydeden", { ad: imza.recordedByName })}` : ""}
          </div>
          {imza.note && <div className="text-muted" style={{ fontSize: 11 }}>{imza.note}</div>}
          {imza.electricianMeslek === false && (
            <div className="text-warning" style={{ fontSize: 11 }}>{t("taahhutnameDurumu.elektrikci.meslekUyari")}</div>
          )}
          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
            {imza.photo ? (
              <Button
                color="link" size="sm" className="p-0"
                onClick={() => onPreview({ originalFileName: imza.photo.originalFileName, storedFileName: imza.photo.storedFileName })}
              >
                <Camera size={12} className="me-1" />{t("taahhutnameDurumu.elektrikci.fotoyuAc")}
              </Button>
            ) : (
              <span className="text-muted fst-italic" style={{ fontSize: 11 }}>{t("taahhutnameDurumu.elektrikci.fotoYok")}</span>
            )}
            <label className="btn btn-sm btn-light mb-0" aria-disabled={busy}>
              {imza.photo ? t("taahhutnameDurumu.elektrikci.fotoyuDegistir") : t("taahhutnameDurumu.elektrikci.fotoEkle")}
              <input
                type="file" className="d-none" accept="image/*,application/pdf" disabled={busy} data-testid="elektrikci-foto-girdi"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFoto(f); e.target.value = ""; }}
              />
            </label>
            <Button color="danger" size="sm" outline disabled={busy} onClick={() => setKaldiriyor(true)} data-testid="elektrikci-kaldir">
              <Trash2 size={12} className="me-1" />{t("taahhutnameDurumu.elektrikci.imzayiKaldir")}
            </Button>
          </div>
          {kaldiriyor && (
            <div className="mt-2" data-testid="elektrikci-kaldir-formu">
              <Input
                value={kaldirNotu} maxLength={300} bsSize="sm"
                placeholder={t("taahhutnameDurumu.elektrikci.kaldirmaNotu")}
                onChange={(e) => setKaldirNotu(e.target.value)}
              />
              <div className="d-flex gap-2 mt-1">
                <Button
                  color="danger" size="sm" disabled={busy} data-testid="elektrikci-kaldir-onay"
                  onClick={async () => { await onKaldir(kaldirNotu.trim()); setKaldiriyor(false); setKaldirNotu(""); }}
                >
                  {t("taahhutnameDurumu.elektrikci.imzayiKaldir")}
                </Button>
                <Button color="light" size="sm" onClick={() => { setKaldiriyor(false); setKaldirNotu(""); }}>{t("common:iptal")}</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!imza && tn.canRecordElektrikci && (
        <Button color="primary" size="sm" outline className="mt-2" disabled={busy} onClick={onAc} data-testid="elektrikci-ac">
          <UserCheck size={13} className="me-1" />{t("taahhutnameDurumu.elektrikci.dugme")}
        </Button>
      )}
    </div>
  );
}

function Gecmis({ kayitlar, anahtar }) {
  const { t } = useTranslation("projeler");
  const [acik, setAcik] = useState(false);
  return (
    <div className="mt-3 pt-2 border-top">
      <Button color="link" size="sm" className="p-0" onClick={() => setAcik((a) => !a)} data-testid={`gecmis-ac-${anahtar}`}>
        <History size={13} className="me-1" />{t("taahhutnameDurumu.gecmis", { adet: kayitlar.length })}
      </Button>
      {acik && (
        kayitlar.length === 0 ? (
          <div className="text-muted small fst-italic mt-1">{t("taahhutnameDurumu.gecmisYok")}</div>
        ) : (
          <ul className="list-unstyled small mb-0 mt-1" data-testid={`gecmis-${anahtar}`}>
            {kayitlar.map((h, i) => (
              <li key={`${h.at}-${h.event}-${i}`} className="py-1 border-bottom">
                <div className="fw-medium">{t(olayAnahtari(h.event))}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {fmtZaman(h.at)} · {h.byStaff ? (h.userName ?? t("taahhutnameDurumu.personel")) : (h.userName ?? t("taahhutnameDurumu.firma"))}
                  {h.surumNo != null ? ` · ${t("taahhutnameDurumu.metinSurumu", { no: h.surumNo })}` : ""}
                </div>
                {h.note && <div className="text-muted" style={{ fontSize: 11 }}>{h.note}</div>}
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

function Kart({ tn, data, uploadedFiles, busy, onChoose, onReviewFile, onPreview, onBelge, onElektrikciAc, onElektrikciKaldir, onElektrikciFoto }) {
  const { t } = useTranslation("projeler");
  const meta = COMMITMENT_STATE[tn.state] ?? COMMITMENT_STATE.NO_SOURCE;
  const Icon = COMMITMENT_ICONS[tn.key] ?? FileText;
  const kaynak = tn.source;
  const adaylar = tn.required && tn.state === "SOURCE_UPLOADED" ? kaynakAdaylari(tn.key, uploadedFiles) : [];

  return (
    <Card className={`h-100 ${tn.required ? "" : "bg-light"}`} data-testid={`taahhutname-kart-${tn.key}`}>
      <CardBody>
        <div className="d-flex align-items-center gap-2 mb-2">
          <StatusTile icon={Icon} color={meta.color} label={`${tn.label} - ${meta.label}`} />
          <div className="min-w-0">
            <div className="fw-semibold">{tn.label}</div>
            <Badge color={meta.color} className="fw-normal" data-testid={`durum-${tn.key}`}>{meta.label}</Badge>
            {tn.satisfied && (
              <Badge color="success" className="fw-normal ms-1"><Check size={11} className="me-1" />{t("taahhutnameDurumu.sartiSagliyor")}</Badge>
            )}
          </div>
        </div>

        {!tn.required && <div className="text-muted small fst-italic mb-2">{t("taahhutnameDurumu.gerekmiyor")}</div>}

        <div className="small fw-semibold mb-1">{t("taahhutnameDurumu.kaynak")}</div>
        {kaynak ? (
          <div className="small mb-2">
            <Button
              color="link" size="sm" className="p-0 text-start" title={kaynak.originalFileName}
              onClick={() => onPreview({ originalFileName: kaynak.originalFileName, storedFileName: kaynak.storedFileName })}
            >
              <ExternalLink size={12} className="me-1" />{kaynak.sourceName ?? kaynak.originalFileName}
            </Button>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {t("taahhutnameDurumu.kaynakSecildi", { ad: kaynak.chosenByName ?? "—", tarih: fmtZaman(kaynak.chosenAt) })}
            </div>
          </div>
        ) : adaylar.length > 0 ? (
          <div className="d-flex flex-column align-items-start gap-1 mb-2">
            <div className="text-muted fst-italic" style={{ fontSize: 11 }}>{t("taahhutnameDurumu.kaynakSecin")}</div>
            {adaylar.map((f) => (
              <Button
                key={f.id} color="primary" outline size="sm" disabled={busy} title={f.originalFileName}
                onClick={() => onChoose(tn.key, f.id)} data-testid={`kaynak-sec-${f.id}`}
              >
                <Check size={13} className="me-1" />
                <span className="text-truncate d-inline-block align-bottom" style={{ maxWidth: 170 }}>{f.originalFileName}</span>
              </Button>
            ))}
          </div>
        ) : (
          <div className="text-muted small fst-italic mb-2">{t("taahhutnameDurumu.kaynakYok")}</div>
        )}

        {kaynak && (
          <Button color="info" outline size="sm" className="mb-2" onClick={() => onBelge(tn.key)} data-testid={`belge-goster-${tn.key}`}>
            <FileText size={13} className="me-1" />{t("taahhutnameDurumu.taahhutnameyiGoster")}
          </Button>
        )}

        <div className="small fw-semibold mt-2 mb-1">{t("taahhutnameDurumu.firmaOnayi")}</div>
        {tn.companyApproval ? (
          <div className="small mb-2" data-testid={`firma-onayi-${tn.key}`}>
            <Check size={13} className="text-success me-1" />
            {t("taahhutnameDurumu.firmaOnayiVar", {
              ad: tn.companyApproval.approvedByName ?? "—",
              tarih: fmtZaman(tn.companyApproval.approvedAt),
              surum: tn.companyApproval.surumNo ?? "?",
            })}
            {tn.companyApproval.currentVersion === false && (
              <div className="text-muted" style={{ fontSize: 11 }}>{t("taahhutnameDurumu.yeniMetinVar")}</div>
            )}
          </div>
        ) : (
          <div className="text-muted small fst-italic mb-2" data-testid={`firma-onayi-${tn.key}`}>
            {/* an approved signed copy is enough for the permit: the company has nothing left to approve (older projects live here) */}
            {tn.state === "SIGNED"
              ? t("taahhutnameDurumu.firmaOnayiGerekmiyor")
              : kaynak ? t("taahhutnameDurumu.firmaOnayiBekleniyor") : t("taahhutnameDurumu.firmaOnayiIcinKaynak")}
          </div>
        )}

        <div className="small fw-semibold mt-2 mb-1">{t("taahhutnameDurumu.imzaliKopya")}</div>
        <ImzaliKopya kopya={tn.signedCopy} busy={busy} onReviewFile={onReviewFile} onPreview={onPreview} />

        {tn.key === "elektrik" && (
          <ElektrikciBolumu tn={tn} busy={busy} onAc={onElektrikciAc} onKaldir={onElektrikciKaldir} onFoto={onElektrikciFoto} onPreview={onPreview} />
        )}

        <Gecmis kayitlar={gecmisOf(data, tn.key)} anahtar={tn.key} />
      </CardBody>
    </Card>
  );
}

export default function TaahhutnameDurumuPaneli({
  data, hata, onYenile, uploadedFiles, actionLoading, projectId,
  onChoose, onReviewFile, onPreview, onElektrikciKaydet, onElektrikciKaldir, onElektrikciFoto,
}) {
  const { t } = useTranslation("projeler");
  const [belgeKey, setBelgeKey] = useState(null);
  const [elektrikciAcik, setElektrikciAcik] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);

  if (!data) {
    return hata ? (
      <Alert color="danger" className="w-100 mb-0" data-testid="taahhut-hata">
        {t("taahhutnameDurumu.yuklenemedi", { mesaj: hata })}{" "}
        {onYenile && <Button color="link" size="sm" className="p-0 align-baseline" onClick={onYenile}>{t("taahhutnameDurumu.tekrarDene")}</Button>}
      </Alert>
    ) : (
      <div className="text-center py-4 w-100" data-testid="taahhut-yukleniyor"><Spinner size="sm" /></div>
    );
  }

  const ozet = sartOzeti(data);
  const belgeTn = belgeKey ? (data.taahhutnameler || []).find((x) => x.key === belgeKey) : null;

  // The page's handlers throw the backend text; the panel shows it above the cards instead of losing it.
  const calistir = (fn) => async (...args) => {
    setIslemHatasi(null);
    try {
      return await fn(...args);
    } catch (e) {
      setIslemHatasi(e.message);
      return undefined;
    }
  };

  return (
    <div className="w-100" data-testid="taahhutname-paneli">
      {hata && <Alert color="warning">{t("taahhutnameDurumu.yenilenemedi", { mesaj: hata })}</Alert>}
      {islemHatasi && <Alert color="danger" toggle={() => setIslemHatasi(null)} data-testid="taahhut-islem-hatasi">{islemHatasi}</Alert>}
      {(data.uyarilar || []).map((u) => <Alert key={u} color="warning">{u}</Alert>)}

      <div className="d-flex align-items-center flex-wrap gap-2 mb-3" data-testid="sart-ozeti">
        {ozet.toplam === 0 ? (
          <span className="text-muted small">{t("taahhutnameDurumu.hicGerekmiyor")}</span>
        ) : (
          <>
            <Badge color={ozet.hepsiTamam ? "success" : "warning"} className="fw-normal">
              {t("taahhutnameDurumu.sartOzeti", { tamam: ozet.tamam, toplam: ozet.toplam })}
            </Badge>
            {ozet.hepsiTamam && (
              <span className="small text-success"><Check size={13} className="me-1" />{t("taahhutnameDurumu.sartSaglandi")}</span>
            )}
          </>
        )}
      </div>

      <Row>
        {(data.taahhutnameler || []).map((tn) => (
          <Col md={4} className="mb-3" key={tn.key}>
            <Kart
              tn={tn}
              data={data}
              uploadedFiles={uploadedFiles}
              busy={actionLoading}
              onChoose={calistir(onChoose)}
              onReviewFile={calistir(onReviewFile)}
              onPreview={onPreview}
              onBelge={setBelgeKey}
              onElektrikciAc={() => setElektrikciAcik(true)}
              onElektrikciKaldir={calistir(onElektrikciKaldir)}
              onElektrikciFoto={calistir(onElektrikciFoto)}
            />
          </Col>
        ))}
      </Row>

      {belgeTn && (
        <Modal isOpen toggle={() => setBelgeKey(null)} size="lg" data-testid="belge-modal">
          <ModalHeader toggle={() => setBelgeKey(null)}>{belgeTn.label}</ModalHeader>
          <ModalBody>
            <TaahhutnameBelgesi header={data.header} taahhut={belgeTn} onPreview={onPreview} projectId={projectId ?? data.projectId} />
          </ModalBody>
        </Modal>
      )}

      {elektrikciAcik && (
        <ElektrikciImzaModal
          isOpen
          toggle={() => setElektrikciAcik(false)}
          header={data.header}
          fuarId={data.fuarId}
          onKaydet={onElektrikciKaydet}
        />
      )}
    </div>
  );
}
